type CompositionState = {
  text: string;
  boxesPerLine: number;
  previewFont: PreviewFontOption;
};

type PreviewFontOption =
  | "default"
  | "klee-one"
  | "yomogi"
  | "yuji-boku"
  | "hachi-maru-pop";

const STORAGE_KEY = "composition-boxes-per-line";
const PREVIEW_FONT_KEY = "composition-preview-font";
const DEFAULT_BOXES_PER_LINE = 15;
const MIN_BOXES_PER_LINE = 1;
const MAX_BOXES_PER_LINE = 50;
const PERFORMANCE_WARNING_THRESHOLD = 1000;
const DEFAULT_PREVIEW_FONT: PreviewFontOption = "default";

const PREVIEW_FONT_CLASSES: Record<PreviewFontOption, string | null> = {
  default: null,
  "klee-one": "preview--klee-one",
  yomogi: "preview--yomogi",
  "yuji-boku": "preview--yuji-boku",
  "hachi-maru-pop": "preview--hachi-maru-pop",
};

const textareaElement = document.getElementById(
  "composition-text",
) as HTMLTextAreaElement;
const boxesPerLineElement = document.getElementById(
  "boxes-per-line",
) as HTMLInputElement;
const previewFontElement = document.getElementById(
  "preview-font",
) as HTMLSelectElement;
const previewPanelElement = document.querySelector(
  ".preview-panel",
) as HTMLDivElement;
const previewElement = document.getElementById("preview") as HTMLDivElement;
const characterCountElement = document.getElementById(
  "character-count",
) as HTMLParagraphElement;
const characterWarningElement = document.getElementById(
  "character-warning",
) as HTMLParagraphElement;
const printPreviewButtonElement = document.getElementById(
  "print-preview",
) as HTMLButtonElement;

const state: CompositionState = {
  text: textareaElement.value,
  boxesPerLine: getStoredBoxesPerLine(),
  previewFont: getStoredPreviewFont(),
};

boxesPerLineElement.value = String(state.boxesPerLine);
previewFontElement.value = state.previewFont;
previewElement.style.setProperty(
  "--boxes-per-line",
  String(state.boxesPerLine),
);
applyPreviewFontClass(state.previewFont);

function getStoredBoxesPerLine() {
  try {
    const storedValue = localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return DEFAULT_BOXES_PER_LINE;
    }

    return clampBoxesPerLine(Number.parseInt(storedValue, 10));
  } catch {
    return DEFAULT_BOXES_PER_LINE;
  }
}

function clampBoxesPerLine(value: number) {
  if (!Number.isFinite(value)) {
    return DEFAULT_BOXES_PER_LINE;
  }

  return Math.min(MAX_BOXES_PER_LINE, Math.max(MIN_BOXES_PER_LINE, value));
}

function persistBoxesPerLine(value: number) {
  try {
    localStorage.setItem(STORAGE_KEY, String(value));
  } catch {
    // Ignore storage failures.
  }
}

function getStoredPreviewFont(): PreviewFontOption {
  try {
    const storedValue = localStorage.getItem(PREVIEW_FONT_KEY);
    if (
      storedValue === "default" ||
      storedValue === "klee-one" ||
      storedValue === "yomogi" ||
      storedValue === "yuji-boku" ||
      storedValue === "hachi-maru-pop"
    ) {
      return storedValue;
    }
  } catch {
    // Ignore storage failures.
  }

  return DEFAULT_PREVIEW_FONT;
}

function persistPreviewFont(value: PreviewFontOption) {
  try {
    localStorage.setItem(PREVIEW_FONT_KEY, value);
  } catch {
    // Ignore storage failures.
  }
}

function applyPreviewFontClass(selectedFont: PreviewFontOption) {
  for (const className of Object.values(PREVIEW_FONT_CLASSES)) {
    if (className) {
      previewElement.classList.remove(className);
    }
  }

  const nextClass = PREVIEW_FONT_CLASSES[selectedFont];
  if (nextClass) {
    previewElement.classList.add(nextClass);
  }
}

function sanitizeInputText(text: string) {
  return text.replace(/[ \t\u3000]/g, "");
}

function isPunctuationChar(char: string) {
  return /[\p{P}\p{S}]/u.test(char);
}

function applyParagraphRules(line: string, boxesPerLine: number) {
  if (line.length === 0) {
    return [] as string[];
  }

  const chars = line.split("");

  // Add one leading empty box for new paragraphs unless punctuation starts it.
  if (!isPunctuationChar(chars[0])) {
    chars.unshift("");
  }

  // If non-punctuation content lands exactly at row end, fold trailing punctuation
  // into the final non-empty box on that row.
  const trailingPunctuation: string[] = [];
  while (chars.length > 0 && isPunctuationChar(chars[chars.length - 1])) {
    trailingPunctuation.unshift(chars.pop() as string);
  }

  if (trailingPunctuation.length === 0) {
    return chars;
  }

  if (chars.length > 0 && chars.length % boxesPerLine === 0) {
    const lastIndex = chars.length - 1;
    chars[lastIndex] = `${chars[lastIndex]}${trailingPunctuation.join("")}`;
    return chars;
  }

  return chars.concat(trailingPunctuation);
}

function getCharacterCount(text: string) {
  let count = 0;

  for (const char of text) {
    if (/^[\p{L}\p{N}]$/u.test(char)) {
      count++;
    }
  }

  return count;
}

function getRows(text: string, boxesPerLine: number) {
  const lines = text.length > 0 ? text.split(/\r?\n/) : [""];
  const rows: string[][] = [];

  for (const line of lines) {
    const cleanedLine = sanitizeInputText(line);

    const paragraphChars = applyParagraphRules(cleanedLine, boxesPerLine);

    if (paragraphChars.length === 0) {
      rows.push(Array.from({ length: boxesPerLine }, () => ""));
      continue;
    }

    for (let index = 0; index < paragraphChars.length; index += boxesPerLine) {
      const cells = paragraphChars.slice(index, index + boxesPerLine);
      while (cells.length < boxesPerLine) {
        cells.push("");
      }
      rows.push(cells);
    }
  }

  return rows;
}

function updateCellSize() {
  const minCellSize = 12;

  const availableWidth = previewPanelElement.clientWidth;
  const columns = state.boxesPerLine;
  const widthPerCell = availableWidth / columns;

  const cellSize = Math.max(minCellSize, widthPerCell);

  previewElement.style.setProperty("--cell-size", `${cellSize}px`);
}

function render() {
  const rows = getRows(state.text, state.boxesPerLine);
  const characterCount = getCharacterCount(state.text);
  const rowCount = rows.length;
  const paragraphCount =
    state.text.length > 0 ? state.text.split(/\r?\n/).length : 1;

  updateCellSize();
  previewElement.replaceChildren();
  previewElement.style.setProperty(
    "--boxes-per-line",
    String(state.boxesPerLine),
  );
  characterCountElement.textContent = `Character count: ${characterCount} | Rows: ${rowCount} | Paragraphs: ${paragraphCount}`;

  if (characterCount > PERFORMANCE_WARNING_THRESHOLD) {
    characterWarningElement.textContent =
      "Warning: performance may start to degrade above 1000 characters.";
    characterWarningElement.classList.remove("is-hidden");
  } else {
    characterWarningElement.textContent = "";
    characterWarningElement.classList.add("is-hidden");
  }

  const fragment = document.createDocumentFragment();

  for (const row of rows) {
    const rowElement = document.createElement("div");
    rowElement.className = "row";

    for (const cellText of row) {
      const cellElement = document.createElement("div");
      cellElement.className = "cell";
      cellElement.textContent = cellText;
      rowElement.appendChild(cellElement);
    }

    fragment.appendChild(rowElement);
  }

  previewElement.appendChild(fragment);
}

function updateText() {
  state.text = textareaElement.value;
  render();
}

function updateBoxesPerLine() {
  const nextValue = getValidBoxesPerLineInputValue();

  if (nextValue === null) {
    return;
  }

  state.boxesPerLine = nextValue;
  persistBoxesPerLine(nextValue);
  render();
}

function getValidBoxesPerLineInputValue() {
  const parsedValue = Number.parseInt(boxesPerLineElement.value, 10);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }

  if (parsedValue < MIN_BOXES_PER_LINE || parsedValue > MAX_BOXES_PER_LINE) {
    return null;
  }

  return parsedValue;
}

function resetBoxesPerLineInput() {
  boxesPerLineElement.value = String(state.boxesPerLine);
}

function updatePreviewFont() {
  const nextFont = previewFontElement.value as PreviewFontOption;
  state.previewFont = nextFont;
  persistPreviewFont(nextFont);
  applyPreviewFontClass(nextFont);
  render();
}

function clearPrintMode() {
  document.body.classList.remove("print-preview-only");
}

function printPreview() {
  document.body.classList.add("print-preview-only");
  window.print();

  // Fallback cleanup for browsers where afterprint is inconsistent.
  setTimeout(() => {
    clearPrintMode();
  }, 300);
}

textareaElement.addEventListener("input", updateText);
boxesPerLineElement.addEventListener("input", updateBoxesPerLine);
boxesPerLineElement.addEventListener("blur", resetBoxesPerLineInput);
previewFontElement.addEventListener("change", updatePreviewFont);
printPreviewButtonElement.addEventListener("click", printPreview);
window.addEventListener("afterprint", clearPrintMode);

render();
previewElement.classList.remove("is-loading");

const resizeObserver = new ResizeObserver(() => {
  render();
});

resizeObserver.observe(previewPanelElement);
